import React, { useEffect, useState, useCallback } from "react";
import CustomTable from "src/components/App/AppGrid/CustomTable/CustomTable";
import { Box, alpha } from "@mui/material";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import theme from "src/config/theme";
import NoDataFound from "../components/NoDataFound/NoDataFound";
import api from "src/api";
import storage from "src/utils/storageUtils";
import useResponsive from "src/components/hooks/useResponsive";
import Welcome from "../components/patient/Welcome";
import { isSanginiRestrictedUser } from "src/utils/sanginiUtils";
import NotAccessible from "src/components/App/NotAccessible";

const columns = [
  { id: "patientName", label: "Patient Name", sx: { textAlign: 'left !important', width: '250px' } },
  { id: "rating", label: "Rating", sx: { justifyContent: 'center !important', width: '100px' } },
  { id: "reviewText", label: "Review", sx: { textAlign: 'left !important', width: '300px' } },
  { id: "dateTime", label: "Date & Time", sx: { justifyContent: 'center !important', width: '200px' } },
];

const ReviewContainer = () => {
  const { isMobile, isTablet } = useResponsive();
  const { clinicId: routeClinicId } = useParams();
  const clinicData = useSelector((state) => state.app?.clinics);
  const currentClinic = clinicData?.currentClinic;
  const clinicId = routeClinicId || currentClinic?.id || storage.get('CLINIC_ID') || storage.get('CLINICID');

  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const fetchReviews = useCallback(async (currentPage = 0, currentPerPage = 10) => {
    setIsLoading(true);
    try {
      // Fetch clinic reviews from the backend API
      const response = await api(`/api/v1/clinic-reviews`, null, 'GET', false, null, 'json', {
        clinicId: clinicId,
        page: currentPage + 1,
        limit: currentPerPage
      });
      
      if (response && response.data) {
        setReviews(response.data);
        setTotalCount(response.meta?.total || 0);
      }
    } catch (error) {
      console.error("Error fetching reviews:", error);
    } finally {
      setIsLoading(false);
    }
  }, [clinicId]);

  useEffect(() => {
    fetchReviews(page, perPage);
  }, [fetchReviews, page, perPage]);

  const data = reviews.map((review) => ({
    id: review.id,
    patientName: review.patientName,
    rating: `${review.rating} ⭐`,
    reviewText: review.reviewText || "-",
    dateTime: review.dateTime,
  }));

  return (
    <Box sx={{
      padding: { xs: '16px', sm: '24px', md: '32px' },
      paddingTop: { xs: '80px', md: '100px' },
      minHeight: '100vh',
      backgroundColor: theme.palette.gery.light3
    }}>
      <Welcome category="reviews" />

      <Box sx={{
        height: 'calc(100vh - 240px)',
        margin: 2,
        boxShadow: isMobile || isTablet ? 'none' : theme.shadows[1],
        borderRadius: '10px',
        border: isMobile || isTablet ? 'none' : `1px solid ${alpha(theme.palette.gery.black, 0.1)}`,
        backgroundColor: isMobile || isTablet ? 'none' : theme.palette.gery.white,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}>
        <CustomTable
          columns={columns}
          data={data}
          page={page}
          onPageChange={(newPage) => setPage(newPage)}
          onPerPageChange={(newPerPage) => {
            setPerPage(newPerPage);
            setPage(0);
          }}
          perPage={perPage}
          totalCount={totalCount}
          loading={isLoading}
          noDataComponent={
            isLoading ? <></> : (
              <NoDataFound
                title="No Reviews Found!"
                description="Patients will submit reviews & ratings after their clinic visits."
                showButton={false}
              />
            )
          }
        />
      </Box>
    </Box>
  );
};

export default ReviewContainer;
